import React, { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { Box, IconButton } from "@mui/material";
import { DefaultInput } from "src/components";
import { ButtonAddOutlined } from "src/components/Button/ButtonAddOutlined";
import { SubmitButton } from "src/components/Button/SubmitButton";
import { SectionHeader } from "src/components/SideBar/CalendarSideBar/components/CreateScheduleMenu/components/Popup/components";
import { PlusIcon } from "src/Icons";
import { TrashBinMenuIconFilled } from "src/Icons/TrashBinMenuIconFilled";
import { closeModal, showModal } from "src/store/reducers/modalSlice";
import { isName, isTitle } from "src/utils/validation";

const DeleteButton = styled(IconButton)({
  marginLeft: "8px",
  color: "#B9B9B9",
  "&:hover, &:active": {
    backgroundColor: "transparent",
  },
});

interface AddSubCategorySectionProps {
  dataSection: any;
  onClose: () => void;
  onSubmit: () => void;
}

export const AddSubCategorySection = ({
  dataSection,
  onClose,
  onSubmit,
}: AddSubCategorySectionProps) => {

  const dispatch = useDispatch();

  const [title, setTitle] = useState<string>(dataSection?.title);

  const [arraySubCategories, setArraySubCategories] = useState<any[]>(
    dataSection?.children
  );

  const formRef = useRef<any>();

  const handleChangeTitle = (newValue: string) => {
    setTitle(newValue);
  };

  const handleChangeSubcategoryName = (newValue: string, id: number) => {
    setArraySubCategories(
      arraySubCategories &&
        arraySubCategories.map((subCategory: any) =>
          +subCategory.id === id
            ? { ...subCategory, title: newValue }
            : subCategory
        )
    );
  };

  const handleSaveDeleteSubCategory = (id: number) => {
    if (id) {
      onClose();
      dispatch(
        showModal({
          modalType: "SUCCESS",
          successText: "Subcategory is successfully deleted",
          okButtonTitle: "Ok, I got it",
          onButtonClick: () => {
            dispatch(closeModal());
            onClose();
          },
        })
      );
    }
  };

  const handleDeleteSubCategory = (id: number) => {
    if (id) {
      onClose();
      dispatch(
        showModal({
          modalType: "DELETE",
          headerTitle: "Are you sure?",
          title:
            "Deleting the service subcategory will result in all assotiated services loosing their subcategory",
          subTitle: "Please, confirm if you want to proceed with this action",
          okButtonTitle: "Delete subcategory",
          onButtonClick: () => {
            setArraySubCategories(
              arraySubCategories &&
                arraySubCategories.filter(
                  (subCategory: any) => +subCategory.id !== id
                )
            );
            dispatch(closeModal());
            handleSaveDeleteSubCategory(id);
          },
        })
      );
    }
  };

  const buttonAvailability = (categoryName: string, subcategoryNames: Array<any>) => {
    const okCategory = isTitle(categoryName)
    const okSubcategories = subcategoryNames.every(subcategory => isTitle(subcategory));
    return (
      okCategory && okSubcategories
    )
  }

  const AddSubcategoryInput = useCallback(() => {
    const categoryFirstChildStyles = {
      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",
    };
    const categoryLastChildStyles = {
      borderBottomLeftRadius: "6px",
      borderBottomRightRadius: "6px",
    };
    return (
      <Box
        sx={{
          marginTop: "32px",
        }}
      >
        <SectionHeader label="Add subcategory" />
        <Box>
          {arraySubCategories &&
            arraySubCategories.map((subCategory: any, index: number) => (
              <Box
                key={+subCategory.id}
                sx={{
                  display: "flex",
                }}
              >
                <DefaultInput
                  placeholder="subcategory"
                  initialValue={subCategory.title}
                  label={
                    arraySubCategories.length > 1
                      ? `Subcategory ${index + 1}`
                      : "Subcategory"
                  }
                  onInputValueChange={(newValue: string) =>
                    handleChangeSubcategoryName(newValue, +subCategory.id)
                  }
                  validationFn={isName}
                  sx={{
                    "&:first-of-type": categoryFirstChildStyles,
                    "&:last-of-type": categoryLastChildStyles,
                  }}
                />
                <DeleteButton
                  onClick={() => handleDeleteSubCategory(+subCategory.id)}
                  disableRipple
                >
                  <TrashBinMenuIconFilled />
                </DeleteButton>
              </Box>
            ))}
        </Box>
      </Box>
    );
  }, [arraySubCategories]);

  const handleAddSubcategory = () => {
    setArraySubCategories([
      ...arraySubCategories,
      {
        id: `${arraySubCategories.length + 1}`,
        title: "",
      },
    ]);
  };

  const handleSaveButtonPress = () => {
    onSubmit();
  };

  return (
    <>
      <Box
        sx={{
          margin: "24px 0 16px",
        }}
      >
        <form ref={formRef}>
          <DefaultInput
            initialValue={title}
            label="Category name"
            placeholder="category"
            onInputValueChange={handleChangeTitle}
            validationFn={isName}
            sx={{
              borderTopRightRadius: "6px",
              borderTopLeftRadius: "6px",
              borderBottomRightRadius: "6px",
              borderBottomLeftRadius: "6px",
            }}
          />
          {AddSubcategoryInput()}
          <ButtonAddOutlined
            title="Add subcategory"
            onClick={handleAddSubcategory}
            leftIcon={<PlusIcon fontSize="inherit" />}
          />
        </form>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <SubmitButton
          variant="outlined"
          title="Cancel"
          onClick={onClose}
          sx={{
            width: "276px",
            borderRadius: "6px",
          }}
        />
        <SubmitButton
          title="Create category"
          disabled={!buttonAvailability(title, arraySubCategories.map((sc: { title: any; }) => sc.title))}
          onClick={handleSaveButtonPress}
          sx={{
            width: "276px",
            borderRadius: "6px",
          }}
        />
      </Box>
    </>
  );
};

